import React from 'react';
import {
  FiMic,
  FiClock,
  FiList,
  FiTarget,
  FiCalendar,
  FiBookOpen,
  FiUsers,
  FiMessageCircle,
} from 'react-icons/fi';
import IELTSCourseTips from '../../../components/IELTSCourseTips';

export default function SpeakingTips() {
  const overview = [
    { icon: <FiClock />, label: 'Time:', value: '11-14 minutes' },
    { icon: <FiList />, label: 'Parts:', value: '3 parts (Introduction, Long Turn, Discussion)' },
    { icon: <FiTarget />, label: 'Skills tested:', value: 'Fluency, vocabulary, grammar, pronunciation' },
  ];

  const tips = [
    {
      title: 'Understand the Three Parts',
      content: [
        '<strong>Part 1 (4-5 min):</strong> General questions about yourself, family, work, studies, interests.',
        '<strong>Part 2 (3-4 min):</strong> Speak for 1-2 minutes on a given topic (cue card).',
        '<strong>Part 3 (4-5 min):</strong> Discussion of abstract ideas related to Part 2 topic.',
      ],
      highlight: 'Tip: Each part tests different aspects of your speaking ability.',
    },
    {
      title: 'Extend Your Answers',
      content: [
        "Don't give one-word answers.",
        'Add reasons, examples, or details.',
        'Use phrases like "because", "for example", "actually".',
      ],
      highlight: 'Tip: Aim to speak for 2-3 sentences per answer in Part 1.',
    },
    {
      title: 'Use a Range of Vocabulary',
      content: [
        'Avoid repeating the same words.',
        'Use topic-specific vocabulary.',
        'Learn collocations and idiomatic expressions.',
      ],
      highlight: 'Tip: Build vocabulary around common IELTS topics.',
      linkText: 'View Vocabulary Lessons',
      linkPath: '/exams/english/#vocabulary',
      linkCategory: 'vocabulary',
    },
    {
      title: 'Show Grammatical Range',
      content: [
        'Use different tenses correctly.',
        'Include complex sentences with conjunctions.',
        'Use conditionals and comparatives when appropriate.',
      ],
      highlight: 'Tip: Accuracy matters, but so does attempting complex structures.',
      linkText: 'View Grammar Lessons',
      linkPath: '/exams/english/#grammar',
      linkCategory: 'grammar',
    },
    {
      title: 'Work on Fluency',
      content: [
        'Speak at a natural pace — not too fast or slow.',
        'Reduce hesitation and pauses.',
        'Use fillers naturally: "well", "you know", "I mean".',
      ],
      highlight: "Tip: Practice speaking without stopping to correct yourself constantly.",
    },
    {
      title: 'Focus on Pronunciation',
      content: [
        'Speak clearly and pronounce words correctly.',
        'Use appropriate word stress and sentence stress.',
        'Practice intonation to express meaning.',
      ],
      highlight: 'Tip: Record yourself and compare with native speakers.',
    },
    {
      title: 'Prepare for Part 2',
      content: [
        'Use the 1-minute preparation time wisely.',
        'Make brief notes on the cue card points.',
        "Structure your talk: opening, main points, conclusion.",
      ],
      highlight: 'Tip: Practice speaking for 2 minutes on random topics.',
    },
    {
      title: 'Engage with Part 3',
      content: [
        'Give thoughtful, developed answers.',
        'Express and justify opinions.',
        'Discuss both sides of an issue if appropriate.',
      ],
      highlight: "Tip: It's okay to take a moment to think before answering.",
    },
  ];

  const extraStrategies = [
    "Stay calm and confident — the examiner wants you to do well.",
    'Ask for clarification if you don\'t understand a question.',
    "If you make a mistake, correct it naturally and move on.",
    'Make eye contact and engage with the examiner.',
  ];

  const improvementTips = [
    { icon: <FiCalendar />, label: 'Daily practice:', text: 'Speak English for at least 15 minutes daily.' },
    { icon: <FiMessageCircle />, label: 'Practice with AI:', text: 'Use Liwaza AI to simulate speaking tests.' },
    { icon: <FiBookOpen />, label: 'Learn phrases:', text: 'Build a bank of useful phrases for different topics.' },
    { icon: <FiUsers />, label: 'Find partners:', text: 'Practice with language exchange partners or study groups.' },
  ];

  const aiHelp = {
    title: 'Need help with IELTS Speaking strategies?',
    firstMessage: 'I need help improving my IELTS Speaking score. Can you help me with fluency and vocabulary?',
    context: 'IELTS Speaking: 11-14 minutes, face-to-face interview with an examiner. Part 1: Introduction and general questions. Part 2: Individual long turn (cue card topic, 1-2 minutes). Part 3: Two-way discussion on abstract topics. Assessed on: Fluency & Coherence, Lexical Resource, Grammatical Range & Accuracy, Pronunciation.',
  };

  return (
    <IELTSCourseTips
      category="speaking"
      icon={<FiMic />}
      title="IELTS Speaking: What You Should Know"
      overview={overview}
      tips={tips}
      extraStrategies={extraStrategies}
      improvementTips={improvementTips}
      aiHelp={aiHelp}
    />
  );
}
